import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AdminSidebar } from "./AdminSidebar";
import { AdminNavTabs } from "./AdminNavTabs";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck } from "lucide-react";
import { CompanySwitcher } from "@/components/CompanySwitcher";
import { UtilityNav } from "@/components/layout/UtilityNav";
import { BrandLogo } from "@/components/ui/BrandLogo";

interface AdminLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showCompanySwitcher?: boolean;
  showNavTabs?: boolean;
}

export const AdminLayout = ({ 
  children, 
  showHeader = true, 
  showCompanySwitcher = false,
  showNavTabs = false 
}: AdminLayoutProps) => {
  return (
    <SidebarProvider defaultOpen={true}>
      <div className="h-screen flex w-full overflow-hidden">
        <AdminSidebar />
        
        {/* Right content area - fixed header + scrollable content */}
        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          {/* Admin Header - Fixed at top, never scrolls */}
          {showHeader && (
            <header className="shrink-0 border-b bg-white shadow-sm z-50">
              <div className="flex items-center px-6 py-4">
                {/* Left: Trigger + Logo + Badge */}
                <div className="flex items-center gap-3">
                  <SidebarTrigger />
                  <Link 
                    to="/dashboard" 
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                  >
                    <BrandLogo variant="compact" size="md" />
                  </Link>
                  <Badge 
                    variant="outline" 
                    className="px-3 py-1 border-amber-500/50 text-amber-600 bg-amber-500/5"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
                    <span className="hidden sm:inline">Admin</span>
                  </Badge>
                </div>

                {/* Spacer to push right content */}
                <div className="flex-1" />

                {/* Center: Company Switcher (optional) */}
                {showCompanySwitcher && (
                  <div className="hidden md:block mr-4">
                    <CompanySwitcher />
                  </div>
                )}

                {/* Right: Utility Navigation - Always right-aligned */}
                <UtilityNav />
              </div>
            </header>
          )}

          {/* Navigation Tabs - Also fixed */}
          {showNavTabs && (
            <div className="shrink-0 border-b bg-white">
              <div className="px-6 py-4">
                <AdminNavTabs />
              </div>
            </div>
          )}

          {/* Main Content - Only this area scrolls */}
          <main className="flex-1 bg-admin-background overflow-y-auto">
            <div className="container max-w-7xl mx-auto px-6 py-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
